import JSZip from "jszip";
import * as parser from 'fast-xml-parser';
import { Midi } from '@tonejs/midi'
import { calculateNotesDuration, calculatePianoNote } from "@/util/util";
import Game from '@/model/game';
import { applyKey } from "@/util/piano-util";
import { mapKey } from "@/util/font-util";
import { loadMetadata } from "@/loader/meta";
import { EVENT_BUS } from '@/model/event-bus';
import { prompt } from '@/ui/dialog'

const PARSER_CONFIG = {
    ignoreAttributes: false,
    ignoreNameSpace: false
};
const MIN_PIANO_SIZE = 24;
const MY_HIGH_RES_PPQ = 960;
const MY_HIGH_RES_PULSE_PER_WHOLE_NOTE = MY_HIGH_RES_PPQ * 4; // for PPQ 960 64th note is 60 which is a goood integer

const PPQ_DURATION_TO_NOTE_TYPE = {
    3840: 'whole',
    1920: 'half',
    960: 'quarter',
    480: 'eighth',
    240: '16th',
    120: '32nd',
    60: '64th'
};
/**
 * Loads an MXL file, extracts XML score from it
 * @param {MXL zip file} blob 
 * @returns XML
 */
export async function loadXmlText(blob) {
    const zip = new JSZip();
    const zipLoadedAsync = zip.loadAsync(blob);
    const text = await zipLoadedAsync.then(
        async (zip) => {
            const containerText = await zip.file("META-INF/container.xml").async("text");
            const { container: rootfiles } = await parser.parse(containerText, {
                ignoreAttributes: false,
                supressEmptyNode: false,
                allowBooleanAttributes: true,
                parseNodeValue: true,
                parseAttributeValue: true,

            });
            const fileName = rootfiles.rootfiles.rootfile['@_full-path'];
            return zip.file(fileName).async("text");
        }
    )
        .catch(err => console.error(err));
    return text;
}

export async function loadChart(pathToFile) {
    return fetch(pathToFile, {cache: "no-store"})
        .then((response) => response.blob())
        .then((blob) => loadXmlText(blob))
        .then((text) => parser.parse(text, PARSER_CONFIG))
        .then((json) => validateMusicXml(json))
        .then((json) => extractMeasures(json))
        .then((measures) => transformToNotes(measures, pathToFile))
}

function extractMeasures(json) {
    const parts = json['score-partwise'].part;
    let measures;
    if (Array.isArray(parts)) {
        // search for piano in part-list
        const partList = json['score-partwise']['part-list']['score-part'];
        const piano = partList.find(p => p['part-name'] && p['part-name'].toLowerCase().includes('piano'));
        if (piano) {
            measures = parts.find(part => part['@_id'] === piano['@_id']).measure;
        } else {
            measures = parts[0].measure;
        }
    }
    else {
        measures = json['score-partwise'].part.measure;
    }
    return measures;
}

async function transformToNotes(measures, pathToFile) {
    const isUserSong = pathToFile.includes('localhost:9990');
    const attributes = Array.isArray(measures.find(m => m.attributes).attributes) ? measures.find(m => m.attributes).attributes[0] : measures.find(m => m.attributes).attributes;
    const direction = measures.find(m => m.direction)?.direction;

    const beats = attributes.time.beats;
    const beatType = attributes.time['beat-type'];
    const divisionsPerQuarterNote = attributes.divisions;
    const key = attributes.key;

    // try loading metadata 
    let meta = {};
    let metadataUrl = pathToFile + '.metadata.json';

    try {
        meta = await fetch(metadataUrl, {cache: "no-store"})
            .then(res => res.json())
            .then(json => {
                if (isUserSong) {
                    json.basepath = 'http://localhost:9990/' + json.basepath
                }
                return loadMetadata(json);
            })
    } catch (e) {
        console.error(e)
        // no metadata is alright too - just play mxl
    }

    if (direction?.sound) {
        Game.bpm = direction.sound['@tempo'] || direction.sound['@_tempo'];
        if (Game.practiceMode === true && !meta?.audio) {
            Game.bpm = Game.bpm * Game.practiceModeTempoPercent / 100;
        }
    }

    let notes = [];
    // fill-in defaults 
    let noteId = 0;
    measures.forEach((section, barIndex) => {
        // section.note can be array, or single obj for one element
        if (section?.note && !section?.note?.forEach) {
            section.note = [section.note];
        }

        section.note.forEach((note) => {
            if (!note.staff) {
                note.staff = 1;
            }
            note.id = noteId++;
            notes.push(note);
        });
    })
    // calculate time and all
    notes = calculateNotesDuration(notes, Game.bpm, divisionsPerQuarterNote);
    notes.forEach((note) => {
        // add count-in time 
        // note.timeSeconds += Game.countInTime;

        if (note.pitch) {
            // add piano key number and realPitch (apply key)
            note.realPitch = {
                octave: note.pitch.octave,
                step: applyKey(note.pitch, mapKey(key)).step
            }
            note.pianoNote = calculatePianoNote(note.realPitch);
        }
    });
    const min = Math.min(...notes.map(note => note.pianoNote).filter(pianoNote => typeof pianoNote === 'number'));
    let max = Math.max(...notes.map(note => note.pianoNote).filter(pianoNote => typeof pianoNote === 'number'));
    if (max - min < MIN_PIANO_SIZE) {
        max += MIN_PIANO_SIZE - (max - min);
    }
    const boundaries = { min, max };
    return {
        boundaries, meta, measures, attributes, beats, beatType, divisionsPerQuarterNote, key, notes
    };
}

export async function loadMidi(filePath) {
    const midi = await Midi.fromUrl(filePath).catch(e => {
        console.error(e);
        EVENT_BUS.emit('error', e);
    });
    const bpm = midi.header?.tempos?.bpm || 120;
    const ppq = midi.header?.ppq || 96;
    // if (meta.bpm) {
    //     // override bpm 
    //     midi.header.setTempo(meta.bpm);
    // }
    const notesToRender = [];
    const bars = midi.duration / 60 * bpm;
    const getBarNumber = (time) => {
        return Math.ceil(time / midi.duration * bars);
    }
    //get the tracks
    // temporal workaround to chose track
    let trackIdx = 0;
    if (midi.tracks.length > 1) {
        try {
            const userIdx = await prompt(
                'Choose the track',
                `More than one track present! \nAvailable midi tracks are:\n${midi.tracks.map((t, i) => i + '.' + t.instrument?.name || 'track ' + i).join('\n') + 'Enter track number \n (default 0)'}`, 0)
            if (userIdx >= 0 && userIdx <= midi.tracks.length - 1) {
                trackIdx = userIdx;
            } else {
                alert('Track out of bounds, 0 is gonna be used');
            }
        } catch (e) {
            // rejected 
            trackIdx = 0;
        }
    }
    const track = midi.tracks[trackIdx]
    //tracks have notes and controlChanges
    //notes are an array
    const instrumentName = track.instrument.name;

    const notes = track.notes
    let noteId = 0;
    notes.forEach((note) => {
        const { time, name, duration, midi } = note;
        const step = name.split(/[0-9]/)[0];
        const octave = Number(name.replace(step, ''));
        notesToRender.push({
            ...note,
            id: noteId++,
            pitch: { step, octave },
            pianoNote: calculatePianoNote({ octave, step }),
            durationSeconds: duration,
            timeSeconds: time,
            instrumentName,
            name,
            midi,
            bar: getBarNumber(time)
        })
    });
    const min = Math.min(...notesToRender.map(note => note.pianoNote).filter(pianoNote => typeof pianoNote === 'number'));
    let max = Math.max(...notesToRender.map(note => note.pianoNote).filter(pianoNote => typeof pianoNote === 'number'));
    if (max - min < MIN_PIANO_SIZE) {
        max += MIN_PIANO_SIZE - (max - min);
    }
    const boundaries = { min, max };
    const measures = buildMeasuresFromMidiNotes(notesToRender, ppq);
    return {
        measures,
        boundaries,
        attributes: {

        },
        beats: 4,
        beatType: 4,
        divisionsPerQuarterNote: MY_HIGH_RES_PPQ,
        key: 'C',
        notes: notesToRender,
        meta: null
    }
}
// validate MXL for feature support
export async function validateMusicXml(json) {
    const parts = json['score-partwise'].part;
    let measures;
    if (Array.isArray(parts)) {
        // search for piano in part-list
        const partList = json['score-partwise']['part-list']['score-part'];
        const piano = partList.find(p => p['part-name'] && p['part-name'].toLowerCase().includes('piano'));
        if (piano) {
            measures = parts.find(part => part['@_id'] === piano['@_id']).measure;
        } else {
            measures = parts[0].measure;
        }
    }
    else {
        measures = json['score-partwise'].part.measure;
    }
    const repeat = measures.find((measure) => {
        return !!measure?.barline?.repeat;
    });
    if (repeat) {
        return Promise.reject(new Error('MXL file contains a <repeat> \nwhich is not supported at the moment.\nAs a possible solution:\ncopy measure twice in musescore '));
    }

    return Promise.resolve(json);
}

function buildMeasuresFromMidiNotes(notes, ppq) {
    const measures = [];
    let section = {
        attributes: {
            time: {
                beats: 4,
                'beat-type': 4
            }
        },
        note: []
    }, currentBar = 0;
    for (let i = 0; i < notes.length; i++) {
        const note = notes[i];
        if (note.bar > currentBar) {
            measures.push(section);
            section = {
                note: []
            };
            currentBar++;
        }
        note.duration = 1 / 4 * ppq / note.durationTicks * MY_HIGH_RES_PULSE_PER_WHOLE_NOTE;
        note.type = PPQ_DURATION_TO_NOTE_TYPE[note.duration];

        if (!note.type) {
            console.warn(`Can not map note type for ${JSON.stringify(note, null, 2)}`)
        }
        section.note.push(note);
    }
    // last section
    measures.push(section);
    return measures;
}